import { ReactNode, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Redirect } from "react-router-dom";

import { useOrg } from "@/contexts/OrgContext";
import { RootState } from "@/types/RootState";
import type { RouteCondition } from "@/types/RouteConfig";

interface RouteAccessGuardProps {
  /** Access condition from RouteConfig.access — a plain predicate over the
   *  route access context, sync or async. */
  condition: RouteCondition;
  children: ReactNode;
}

/**
 * Generic route-level access guard (see {@link RouteCondition}). Redirects
 * to the standard /no_access page when the condition fails — the same
 * destination the menu-based protection uses, so deep links and hidden-menu
 * subPages get the platform's normal "no access" UX. Renders nothing while
 * an async condition is in flight (no content flash) and fails closed when
 * a condition throws.
 */
const RouteAccessGuard = ({ condition, children }: RouteAccessGuardProps) => {
  const loginInfo = useSelector((state: RootState) => state.user);
  const { orgId } = useOrg();
  const [allowed, setAllowed] = useState<boolean | null>(null);

  useEffect(() => {
    let cancelled = false;
    setAllowed(null);
    Promise.resolve(condition({ loginInfo, orgId }))
      .then((ok) => {
        if (!cancelled) setAllowed(ok);
      })
      .catch(() => {
        if (!cancelled) setAllowed(false);
      });
    return () => {
      cancelled = true;
    };
    // Re-evaluate when the browsed org changes; loginInfo is stable post-login
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId]);

  if (allowed === null) {
    return null; // condition in flight — avoid flashing the protected page
  }
  if (!allowed) {
    return <Redirect to="/no_access" />;
  }
  return <>{children}</>;
};

export default RouteAccessGuard;
